import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import {
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  Star,
  X
} from 'lucide-react';

// Multi-select Combobox for categories/certifications
const MultiSelectCombobox = ({ options, value = [], onChange, placeholder, disabled }) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(inputValue.toLowerCase()) &&
    !value.includes(option)
  );

  const handleAdd = (option) => {
    onChange([...value, option]);
    setInputValue('');
    setIsOpen(false);
  };

  const handleRemove = (option) => {
    onChange(value.filter(v => v !== option));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim() && !options.includes(inputValue)) {
      e.preventDefault();
      handleAdd(inputValue.trim());
    }
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap gap-1 p-2 border rounded-lg min-h-[42px]">
        {value.map((item, index) => (
          <Badge key={index} variant="outline" className="gap-1">
            {item}
            {!disabled && (
              <button
                type="button"
                onClick={() => handleRemove(item)}
                className="ml-1 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
        {!disabled && (
          <input
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setTimeout(() => setIsOpen(false), 200)}
            onKeyDown={handleKeyDown}
            placeholder={value.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[120px] outline-none text-sm bg-transparent"
          />
        )}
      </div>
      {isOpen && filteredOptions.length > 0 && !disabled && (
        <div className="absolute z-10 w-full mt-1 bg-popover border rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filteredOptions.map((option, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleAdd(option)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SupplierFormDialog = ({
  open,
  onClose,
  onSubmit,
  initialData = null,
  mode = 'create' // create, edit, view
}) => {
  const [formData, setFormData] = useState({
    supplier_name: '',
    supplier_code: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    province: '',
    postal_code: '',
    country: 'China',
    website: '',
    business_license: '',
    tax_id: '',
    payment_terms: '',
    currency: 'CNY',
    credit_limit: 0,
    supplier_type: 'manufacturer',
    categories: [],
    certifications: [],
    rating: 3,
    is_active: true,
    is_preferred: false,
    quality_certification: '',
    delivery_terms: '',
    notes: '',
    bank_info: {
      bank_name: '',
      account_number: '',
      swift_code: '',
      account_holder: ''
    },
    performance_metrics: {
      delivery_performance: 0,
      quality_score: 0,
      cost_competitiveness: 0,
      service_quality: 0
    }
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [availableCategories] = useState([
    '电子元器件', '机械零件', '原材料', '包装材料', '工具设备', '办公用品', '其他'
  ]);
  const [availableCertifications] = useState([
    'ISO 9001', 'ISO 14001', 'OHSAS 18001', 'TS 16949', 'CE认证', 'FCC认证', 'RoHS认证', '3C认证'
  ]);

  useEffect(() => {
    if (open) {
      if (initialData) {
        setFormData({
          ...initialData,
          categories: initialData.categories || [],
          certifications: initialData.certifications || [],
          bank_info: initialData.bank_info || {
            bank_name: '',
            account_number: '',
            swift_code: '',
            account_holder: ''
          },
          performance_metrics: initialData.performance_metrics || {
            delivery_performance: 0,
            quality_score: 0,
            cost_competitiveness: 0,
            service_quality: 0
          }
        });
      } else {
        resetForm();
      }
      setErrors({});
    }
  }, [open, initialData]);

  const resetForm = () => {
    setFormData({
      supplier_name: '',
      supplier_code: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      province: '',
      postal_code: '',
      country: 'China',
      website: '',
      business_license: '',
      tax_id: '',
      payment_terms: '',
      currency: 'CNY',
      credit_limit: 0,
      supplier_type: 'manufacturer',
      categories: [],
      certifications: [],
      rating: 3,
      is_active: true,
      is_preferred: false,
      quality_certification: '',
      delivery_terms: '',
      notes: '',
      bank_info: {
        bank_name: '',
        account_number: '',
        swift_code: '',
        account_holder: ''
      },
      performance_metrics: {
        delivery_performance: 0,
        quality_score: 0,
        cost_competitiveness: 0,
        service_quality: 0
      }
    });
  };

  const handleFieldChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear field errors
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.supplier_name || formData.supplier_name.trim() === '') {
      newErrors.supplier_name = '请输入供应商名称';
    }

    if (!formData.contact_person || formData.contact_person.trim() === '') {
      newErrors.contact_person = '请输入联系人姓名';
    }

    if (!formData.phone || formData.phone.trim() === '') {
      newErrors.phone = '请输入联系电话';
    }

    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = '请输入有效的邮箱地址';
    }

    // Credit limit validation
    if (formData.credit_limit < 0) {
      newErrors.credit_limit = '信用额度不能为负数';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
      handleClose();
    } catch (error) {
      console.error('Error submitting supplier data:', error);
      setErrors({ submit: error.response?.data?.error || 'Failed to save supplier' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      setErrors({});
      onClose();
    }
  };

  const isReadOnly = mode === 'view';

  const renderRatingStars = () => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => !isReadOnly && handleFieldChange('rating', star)}
            disabled={isReadOnly}
            className={`p-1 ${isReadOnly ? 'cursor-default' : 'hover:scale-110 transition-transform'}`}
          >
            <Star
              className={`h-5 w-5 ${star <= formData.rating ? 'text-warning fill-yellow-500' : 'text-muted-foreground'}`}
            />
          </button>
        ))}
        <span className="text-sm text-muted-foreground ml-2">{formData.rating}/5</span>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {mode === 'create' ? '新建供应商' : mode === 'edit' ? '编辑供应商' : '供应商详情'}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {errors.submit && (
            <Alert variant="destructive">
              <AlertDescription>{errors.submit}</AlertDescription>
            </Alert>
          )}

          {/* Basic Information */}
          <div>
            <h3 className="font-semibold mb-3">基本信息</h3>
            <Separator className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>供应商名称 *</Label>
                <Input
                  value={formData.supplier_name}
                  onChange={(e) => handleFieldChange('supplier_name', e.target.value)}
                  disabled={isReadOnly}
                  className={errors.supplier_name ? 'border-red-500' : ''}
                />
                {errors.supplier_name && (
                  <p className="text-xs text-destructive">{errors.supplier_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>供应商编码</Label>
                <Input
                  value={formData.supplier_code}
                  onChange={(e) => handleFieldChange('supplier_code', e.target.value)}
                  disabled={isReadOnly}
                  placeholder="系统自动生成或手动输入"
                />
              </div>

              <div className="space-y-2">
                <Label>供应商类型</Label>
                <Select
                  value={formData.supplier_type}
                  onValueChange={(value) => handleFieldChange('supplier_type', value)}
                  disabled={isReadOnly}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manufacturer">制造商</SelectItem>
                    <SelectItem value="distributor">分销商</SelectItem>
                    <SelectItem value="service">服务商</SelectItem>
                    <SelectItem value="other">其他</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>业务类别</Label>
                <MultiSelectCombobox
                  options={availableCategories}
                  value={formData.categories}
                  onChange={(value) => handleFieldChange('categories', value)}
                  placeholder="选择或输入业务类别"
                  disabled={isReadOnly}
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="font-semibold mb-3">联系信息</h3>
            <Separator className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>联系人 *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={formData.contact_person}
                    onChange={(e) => handleFieldChange('contact_person', e.target.value)}
                    disabled={isReadOnly}
                    className={`pl-10 ${errors.contact_person ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.contact_person && (
                  <p className="text-xs text-destructive">{errors.contact_person}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>联系电话 *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                    disabled={isReadOnly}
                    className={`pl-10 ${errors.phone ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.phone && (
                  <p className="text-xs text-destructive">{errors.phone}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>邮箱地址</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={formData.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    disabled={isReadOnly}
                    className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.email && (
                  <p className="text-xs text-destructive">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>官方网站</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => handleFieldChange('website', e.target.value)}
                  disabled={isReadOnly}
                  placeholder="https://www.example.com"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>公司地址</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={formData.address}
                    onChange={(e) => handleFieldChange('address', e.target.value)}
                    disabled={isReadOnly}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>城市</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => handleFieldChange('city', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>

              <div className="space-y-2">
                <Label>省份</Label>
                <Input
                  value={formData.province}
                  onChange={(e) => handleFieldChange('province', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>

              <div className="space-y-2">
                <Label>邮政编码</Label>
                <Input
                  value={formData.postal_code}
                  onChange={(e) => handleFieldChange('postal_code', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div>
            <h3 className="font-semibold mb-3">商务信息</h3>
            <Separator className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>营业执照号</Label>
                <Input
                  value={formData.business_license}
                  onChange={(e) => handleFieldChange('business_license', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>

              <div className="space-y-2">
                <Label>税务登记号</Label>
                <Input
                  value={formData.tax_id}
                  onChange={(e) => handleFieldChange('tax_id', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>

              <div className="space-y-2">
                <Label>付款条款</Label>
                <Input
                  value={formData.payment_terms}
                  onChange={(e) => handleFieldChange('payment_terms', e.target.value)}
                  disabled={isReadOnly}
                  placeholder="例如: 30天付款"
                />
              </div>

              <div className="space-y-2">
                <Label>结算货币</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => handleFieldChange('currency', value)}
                  disabled={isReadOnly}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CNY">人民币 (CNY)</SelectItem>
                    <SelectItem value="USD">美元 (USD)</SelectItem>
                    <SelectItem value="EUR">欧元 (EUR)</SelectItem>
                    <SelectItem value="JPY">日元 (JPY)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>信用额度</Label>
                <Input
                  type="number"
                  value={formData.credit_limit}
                  onChange={(e) => handleFieldChange('credit_limit', parseFloat(e.target.value) || 0)}
                  disabled={isReadOnly}
                  min={0}
                  step={1000}
                  className={errors.credit_limit ? 'border-red-500' : ''}
                />
                {errors.credit_limit && (
                  <p className="text-xs text-destructive">{errors.credit_limit}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>供应商评级</Label>
                {renderRatingStars()}
              </div>
            </div>
          </div>

          {/* Certifications */}
          <div>
            <div className="space-y-2">
              <Label>资质认证</Label>
              <MultiSelectCombobox
                options={availableCertifications}
                value={formData.certifications}
                onChange={(value) => handleFieldChange('certifications', value)}
                placeholder="选择或输入认证资质"
                disabled={isReadOnly}
              />
            </div>
          </div>

          {/* Status Controls */}
          <div className="flex gap-6">
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => handleFieldChange('is_active', checked)}
                disabled={isReadOnly}
              />
              <Label>启用状态</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_preferred}
                onCheckedChange={(checked) => handleFieldChange('is_preferred', checked)}
                disabled={isReadOnly}
              />
              <Label>优选供应商</Label>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>备注信息</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              disabled={isReadOnly}
              rows={3}
              placeholder="记录供应商的特殊要求、合作历史等信息..."
            />
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            {isReadOnly ? '关闭' : '取消'}
          </Button>
          {!isReadOnly && (
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? '保存中...' : (mode === 'edit' ? '更新' : '创建')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SupplierFormDialog;
